import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  History, RefreshCw, Search, ArrowRight, User, UserCog,
  PlusCircle, PencilLine, Trash2, XCircle, RefreshCcw, Repeat, Table2, CheckCircle2
} from 'lucide-react';

// Deutsche Bezeichnung + Farbe/Icon je Aktion.
function actionMeta(action) {
  const a = action || '';
  if (a.startsWith('create')) return { label: 'Angelegt', color: '#16A34A', bg: '#DCFCE7', Icon: PlusCircle };
  if (a === 'delete') return { label: 'Gelöscht', color: '#DC2626', bg: '#FEE2E2', Icon: Trash2 };
  if (a === 'customer_cancel' || a === 'status_cancelled') return { label: 'Storniert', color: '#DC2626', bg: '#FEE2E2', Icon: XCircle };
  if (a === 'status_no_show') return { label: 'No-Show', color: '#DC2626', bg: '#FEE2E2', Icon: XCircle };
  if (a.startsWith('status_')) return { label: 'Status geändert', color: '#0369A1', bg: '#E0F2FE', Icon: CheckCircle2 };
  if (a === 'update') return { label: 'Geändert', color: '#B45309', bg: '#FEF3C7', Icon: PencilLine };
  if (a === 'swap_tables') return { label: 'Tische getauscht', color: '#7C3AED', bg: '#EDE9FE', Icon: Repeat };
  if (a === 'release_table') return { label: 'Tisch freigegeben', color: '#0369A1', bg: '#E0F2FE', Icon: Table2 };
  if (a.includes('rebook')) return { label: 'Umgebucht', color: '#0369A1', bg: '#E0F2FE', Icon: RefreshCcw };
  return { label: a || 'Aktion', color: '#5C5C59', bg: '#F1F5F9', Icon: PencilLine };
}

const ENTITY_DE = {
  reservation: 'Reservierung', area: 'Bereich', table: 'Tisch', offer: 'Angebot',
  settings: 'Einstellungen', special_day: 'Sondertag', opening_hours: 'Öffnungszeiten',
  blocked_period: 'Sperrzeit', backup: 'Backup', feedback: 'Bewertung',
  area_day_closure: 'Bereichssperre', table_combination: 'Tischgruppe',
};

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function AdminActivityLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [scope, setScope] = useState('reservation'); // 'reservation' | 'all'
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams();
      if (scope === 'reservation') params.append('entity_type', 'reservation');
      params.append('limit', '300');
      const r = await api.get(`/audit-log?${params.toString()}`, { timeout: 20000 });
      setEntries(r.data?.entries || []);
    } catch {
      setError(true);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const filtered = entries.filter((e) => {
    if (!q) return true;
    return [e.entity_label, e.actor, e.action, ENTITY_DE[e.entity_type] || e.entity_type]
      .some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="admin-activity-log">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Verwaltung</p>
          <h1 className="font-display text-3xl text-[#1C1C19] flex items-center gap-2">
            <History className="w-7 h-7" /> Aktivitäts-Log
          </h1>
          <p className="text-sm text-[#5C5C59] mt-1">Nachvollziehbarkeit: wer hat wann was geändert, storniert oder angelegt.</p>
        </div>
        <button
          onClick={load}
          data-testid="activity-refresh"
          className="inline-flex items-center gap-2 border border-[#E5E5E0] px-4 py-2.5 rounded text-sm hover:bg-[#F9F9F7] transition"
        >
          <RefreshCw className="w-4 h-4" /> Aktualisieren
        </button>
      </div>

      {/* Umschalter + Suche */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1 bg-[#F1F5F9] rounded-lg p-1" data-testid="activity-scope">
          {[{ key: 'reservation', label: 'Reservierungen' }, { key: 'all', label: 'Alles' }].map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              data-testid={`activity-scope-${s.key}`}
              className={`px-3 py-1.5 rounded-md text-sm transition ${scope === s.key ? 'bg-white text-[#1C1C19] shadow-sm font-medium' : 'text-[#64748B] hover:text-[#1C1C19]'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Gast, Mitarbeiter oder Aktion suchen"
            data-testid="activity-search"
            className="w-full pl-9 pr-3 py-2 border border-[#E5E5E0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C1C19]/10"
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#F1F1EE]" data-testid="activity-list">
        {loading && <div className="px-4 py-10 text-center text-[#9CA3AF]">Wird geladen…</div>}
        {!loading && error && (
          <div className="px-4 py-10 text-center" data-testid="activity-error">
            <p className="text-[#DC2626] font-medium">Das Aktivitäts-Log konnte nicht geladen werden.</p>
            <p className="text-sm text-[#9CA3AF] mt-1">Bitte versuche es erneut.</p>
            <button
              onClick={load}
              className="mt-3 inline-flex items-center gap-2 border border-[#E5E5E0] px-4 py-2 rounded text-sm hover:bg-[#F9F9F7] transition"
            >
              <RefreshCw className="w-4 h-4" /> Erneut laden
            </button>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="px-4 py-10 text-center text-[#9CA3AF]" data-testid="activity-empty">Keine Aktivitäten gefunden.</div>
        )}
        {!loading && !error && filtered.map((e) => {
          const meta = actionMeta(e.action);
          const { Icon } = meta;
          return (
            <div key={e.id} className="px-4 py-3 flex items-start gap-3" data-testid={`activity-row-${e.id}`}>
              <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{ background: meta.bg }}>
                <Icon className="w-4 h-4" style={{ color: meta.color }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: meta.bg, color: meta.color }}>
                    {meta.label}
                  </span>
                  <span className="text-[11px] uppercase tracking-wider text-[#9CA3AF]">{ENTITY_DE[e.entity_type] || e.entity_type}</span>
                  <span className="text-sm font-medium text-[#1C1C19] truncate">{e.entity_label || e.entity_id}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-[#5C5C59]">
                  {e.is_guest
                    ? <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> Gast</span>
                    : <span className="inline-flex items-center gap-1"><UserCog className="w-3 h-3" /> {e.actor}</span>}
                  <span className="text-[#D1D5DB]">·</span>
                  <span>{fmtWhen(e.created_at)}</span>
                </div>
                {e.changes && e.changes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`activity-changes-${e.id}`}>
                    {e.changes.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-[#F9F9F7] border border-[#E5E5E0] rounded px-1.5 py-0.5">
                        <span className="text-[#5C5C59]">{c.label}:</span>
                        <span className="text-[#9CA3AF] line-through">{c.from}</span>
                        <ArrowRight className="w-3 h-3 text-[#9CA3AF]" />
                        <span className="text-[#1C1C19] font-medium">{c.to}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-[#9CA3AF] mt-4">Zeigt die letzten 300 Einträge. Aktionen von Gästen (Online-Buchung/-Stornierung) sind als „Gast" gekennzeichnet.</p>
    </div>
  );
}
